import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { WfPropertyListEntry } from 'wflow-main-app/src/app/shared/dumb-components/property-list/property-list.component';
import {
  findPeppolUblFieldSet,
  PeppolUblFieldDescriptor,
} from '../../../../const/peppol-ubl-field-sets.const';
import { PeppolDetail } from '../../../../interfaces/peppol-detail.interface';
import { PeppolUblPreview } from '../../../../interfaces/peppol-ubl-preview.interface';
import { isPeppolTaxDocument } from '../../../../utils/peppol-document-type.util';
import { PeppolUblPreviewLinesComponent } from '../peppol-ubl-preview-lines/peppol-ubl-preview-lines.component';
import { PeppolUblPreviewVatRecapComponent } from '../peppol-ubl-preview-vat-recap/peppol-ubl-preview-vat-recap.component';

@Component({
  selector: 'wf-peppol-ubl-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    DecimalPipe,
    TranslateModule,
    PeppolUblPreviewLinesComponent,
    PeppolUblPreviewVatRecapComponent,
  ],
  templateUrl: './peppol-ubl-preview.component.html',
  styleUrl: './peppol-ubl-preview.component.scss',
  host: { class: 'wf-peppol-ubl-preview' },
})
export class PeppolUblPreviewComponent {
  readonly ublPreview = input.required<PeppolUblPreview>();
  readonly detail = input.required<PeppolDetail>();

  protected readonly isTaxDocument = computed(() =>
    isPeppolTaxDocument(this.detail().documentType),
  );

  protected readonly fieldSet = computed(() =>
    findPeppolUblFieldSet(this.detail().documentType),
  );

  protected readonly parties = computed(() => {
    const fieldSet = this.fieldSet();
    if (!fieldSet?.showParties) {
      return [];
    }
    const preview = this.ublPreview();
    const detail = this.detail();
    return [
      {
        heading: 'PEPPOL.Preview.Supplier',
        party: preview.sender,
        peppolId: detail.senderId,
        bordered: false,
      },
      {
        heading: 'PEPPOL.Preview.Customer',
        party: preview.receiver,
        peppolId: detail.receiverId,
        bordered: true,
      },
    ].map(({ heading, party, peppolId, bordered }) => ({
      heading,
      bordered,
      name: party?.name,
      rows: fieldSet.partyFields.map((descriptor) => ({
        key: descriptor.key,
        value: descriptor.accessor({ party, peppolId }),
        noLabel: descriptor.noLabel ?? false,
      })),
    }));
  });

  protected readonly paymentEntries = computed(() =>
    this.toEntries(this.fieldSet()?.paymentInfo ?? []),
  );
  protected readonly dateEntries = computed(() =>
    this.toEntries(this.fieldSet()?.dates ?? []),
  );

  private toEntries(
    descriptors: PeppolUblFieldDescriptor[],
  ): WfPropertyListEntry[] {
    const context = { preview: this.ublPreview(), detail: this.detail() };
    return descriptors
      .map((descriptor) => ({
        key: descriptor.key,
        value: descriptor.accessor(context),
        type: descriptor.type,
      }))
      .filter((entry) => entry.value != null && entry.value !== '');
  }
}
